import { Link } from "@tanstack/react-router";
import { LiveDesktop } from "../components/live-desktop";
import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { z } from "zod";
import type { ApprovalView, MissionRunView, RunDetail, RunTimelineEntry } from "../../shared/contracts";
import { ApprovalCard } from "../components/approval-card";
import { LIVE_VIEW_SEARCH } from "../../shared/app-pages";
import { IconArrowLeft, IconCheck, IconEye, IconTool, IconX } from "../components/icons";
import { RichText } from "../components/rich-text";
import { Badge, Button, buttonClass, Card, EmptyState, ErrorNotice, Field, Loading, Notice, Textarea } from "../components/ui";
import { upsertMachine, useMachines, usePendingApprovals } from "../lib/agent";
import { api, ApiError, errorText, fileUrl } from "../lib/api";
import { fmtDateTime, fmtDuration, fmtTime, fmtUsd, isRunActive, machineTargetLabel, toolLabel } from "../lib/format";
import { useNow, usePageTitle } from "../lib/hooks";
import { useAgentEvents } from "../lib/socket";
import { invalidate, mutateResource, useResource } from "../lib/store";
import { RunStatusBadge } from "./pulse";

/** Optional fields some agent versions add to the run view (question id/code for answers by message). */
const runExtras = z.looseObject({
  questionId: z.string().min(1).max(100).optional().catch(undefined),
  questionCode: z.string().regex(/^Q\d{1,4}$/).optional().catch(undefined),
});

function entryKey(e: RunTimelineEntry): string {
  switch (e.kind) {
    case "screenshot":
      return `s:${e.at}:${e.fileKey}`;
    case "action":
      return `a:${e.at}:${e.tool}:${e.summary}`;
    default:
      return `${e.kind}:${e.at}:${e.text}`;
  }
}

function mergeEntries(current: RunTimelineEntry[], entry: RunTimelineEntry): RunTimelineEntry[] {
  const key = entryKey(entry);
  if (current.some((e) => entryKey(e) === key)) return current;
  return [...current, entry].sort((a, b) => a.at - b.at);
}

/** Who carries the run out: Claude (the mission loop) or GPT (screen driving). */
export function ExecutorBadge({ executor }: { executor: "claude" | "openai" }) {
  const label = executor === "openai" ? "GPT · écran" : "Claude";
  const title = executor === "openai" ? "Exécutée par GPT-6.1 Terra en mode rapide, qui pilote l'écran (clics, saisie, défilement)" : "Exécutée par Claude, pas à pas depuis Ariston";
  return (
    <span title={title}>
      <Badge tone={executor === "claude" ? "neutral" : "accent"}>{label}</Badge>
    </span>
  );
}

/**
 * /runs/$id — one mission run in plain words: its status, the question or validation it waits on, its result, the
 * steps so far, the live screen (a link) and « Annuler ». Machine, dates, duration, cost and the agent's reasoning sit
 * behind « Détails techniques ».
 */
export function RunPage({ id }: { id: string }) {
  usePageTitle("Exécution");
  const now = useNow(30_000);
  const detail = useResource<RunDetail>(`run:${id}`, (signal) => api("GET /api/runs/:id", { params: { id }, signal }), {
    refreshOn: (e) => e.type === "run.upsert" && e.run.id === id,
    // Belt and braces while the run is alive: events can be missed during a reconnection.
    pollMs: (d) => (d && isRunActive(d.run.status) ? 5000 : false),
  });
  const machines = useMachines();
  // An open run on the cloud computer: the page opening wakes it from its light sleep (once per visit).
  const woke = useRef(false);
  const runActive = detail.data ? isRunActive(detail.data.run.status) && detail.data.run.machine === "cloud" : false;
  const cloudMachine = machines.data?.find((m) => m.kind === "cloud");
  useEffect(() => {
    if (woke.current || !runActive || !cloudMachine || (cloudMachine.state !== "sleeping" && cloudMachine.state !== "stopped")) return;
    woke.current = true;
    void api("POST /api/machines/:id/wake", { params: { id: cloudMachine.id }, body: { reason: "run_page" } })
      .then(upsertMachine)
      .catch(() => undefined);
  }, [runActive, cloudMachine]);

  useAgentEvents((e) => {
    if (e.type === "run.event" && e.runId === id) {
      detail.mutate((d) => (d ? { ...d, timeline: mergeEntries(d.timeline, e.entry) } : d));
    } else if (e.type === "run.upsert" && e.run.id === id) {
      detail.mutate((d) => (d ? { ...d, run: e.run } : d));
    }
  });

  if (detail.loading) return <Loading />;
  if (!detail.data) {
    if (detail.error?.status === 404) {
      return (
        <EmptyState icon="🔎" title="Exécution introuvable" action={<BackToMissions />}>
          Ce lien ne correspond à aucune de tes exécutions.
        </EmptyState>
      );
    }
    return <ErrorNotice message={detail.error?.message ?? "Impossible de charger cette exécution."} onRetry={() => void detail.reload()} />;
  }

  const { run, timeline } = detail.data;
  const steps = timeline.filter((e) => e.kind !== "thinking");
  const thoughts = timeline.filter((e) => e.kind === "thinking");
  const active = isRunActive(run.status);
  const cloud = run.machine === "cloud" ? machines.data?.find((m) => m.kind === "cloud") : undefined;
  const ended = run.finishedAt ?? (active ? now : null);

  return (
    <div className="mx-auto max-w-2xl space-y-7">
      <div>
        <BackToMissions />
        <h1 className="mt-3 font-display text-[34px] leading-[1.08] font-normal tracking-[-0.02em] sm:text-[40px]">Exécution</h1>
        <p className="mt-1 text-[16px] font-medium wrap-anywhere">{run.title}</p>
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
          <RunStatusBadge status={run.status} />
          {run.executor && run.executor !== "claude" ? <ExecutorBadge executor={run.executor} /> : null}
        </div>
        {run.status === "running" || run.status === "queued" ? <span aria-hidden="true" className="progress-glide mt-3 block max-w-xs" /> : null}
      </div>

      {run.status === "waiting_user" ? <QuestionBox run={run} timeline={timeline} onAnswered={() => void detail.reload()} /> : null}
      {run.status === "waiting_approval" && run.approvalId ? <ApprovalBox approvalId={run.approvalId} /> : null}

      {run.status === "done" && run.result ? (
        <Card className="border-success/40 p-5">
          <h2 className="font-display text-[20px] leading-snug font-medium">Résultat</h2>
          <RichText text={run.result} className="mt-2 text-[14px] leading-relaxed wrap-anywhere" />
        </Card>
      ) : null}
      {run.status === "failed" && run.error ? (
        <Notice tone="danger" role="alert">
          {run.error}
        </Notice>
      ) : null}

      {active && cloud ? (
        // The run's own desktop, live, without a click (several runs: each page shows its own desktop).
        <LiveDesktop machineId={cloud.id} autoConnect followRunId={run.id} />
      ) : null}

      {active ? (
        <div className="flex flex-wrap items-center gap-2">
          {cloud ? (
            <Link to="/ordinateur" search={LIVE_VIEW_SEARCH} className={buttonClass("secondary", "md")}>
              <IconEye width={16} height={16} />
              Ouvrir en grand
            </Link>
          ) : null}
          <CancelRun run={run} onCancelled={(r) => detail.mutate((d) => (d ? { ...d, run: r } : d))} />
        </div>
      ) : null}

      <section aria-labelledby="run-steps">
        <h2 id="run-steps" className="mb-3 text-[13px] font-semibold tracking-[0.04em] text-muted uppercase">
          Étapes
        </h2>
        {steps.length ? (
          <ol className="space-y-2.5">
            {steps.map((entry, i) => (
              <TimelineItem key={`${entryKey(entry)}-${i}`} entry={entry} />
            ))}
          </ol>
        ) : (
          <p className="text-[14px] text-muted">{active ? "Ça démarre…" : "Aucune étape."}</p>
        )}
      </section>

      <details className="group">
        <summary className="inline-flex min-h-11 cursor-pointer list-none items-center gap-1 text-[14px] font-medium text-muted select-none hover:text-fg [&::-webkit-details-marker]:hidden">
          <span>Détails techniques</span>
          <span aria-hidden="true" className="text-[11px] transition group-open:rotate-90">
            ›
          </span>
        </summary>
        <div className="mt-3 space-y-3 rounded-[20px] bg-sunken p-4 text-[13.5px] text-muted">
          <p>
            {machineTargetLabel(run.machine, machines.data)}
            {run.startedAt ? ` · lancée ${fmtDateTime(run.startedAt, now)}` : ` · créée ${fmtDateTime(run.createdAt, now)}`}
            {run.startedAt && ended ? ` · ${fmtDuration(Math.max(0, ended - run.startedAt))}` : ""}
            {run.costUsd ? ` · ${fmtUsd(run.costUsd)}` : ""}
          </p>
          {run.missionId ? (
            <Link to="/missions/$id" params={{ id: run.missionId }} className="inline-flex min-h-11 items-center font-semibold text-accent hover:text-accent-hover sm:min-h-0">
              Voir la mission
            </Link>
          ) : null}
          {thoughts.length ? (
            <ol className="space-y-2">
              {thoughts.map((entry, i) => (
                <TimelineItem key={`${entryKey(entry)}-${i}`} entry={entry} />
              ))}
            </ol>
          ) : null}
        </div>
      </details>
    </div>
  );
}

function BackToMissions() {
  return (
    <Link to="/missions" className="inline-flex items-center gap-1.5 text-[13px] font-medium text-muted hover:text-fg">
      <IconArrowLeft width={15} height={15} />
      Retour
    </Link>
  );
}

function TimelineItem({ entry }: { entry: RunTimelineEntry }) {
  const time = <span className="shrink-0 pt-0.5 font-mono text-[11.5px] text-subtle tabular-nums">{fmtTime(entry.at)}</span>;
  switch (entry.kind) {
    case "text":
      return (
        <li data-testid="run-entry" className="flex gap-3">
          {time}
          <RichText text={entry.text} className="min-w-0 flex-1 text-[14px] leading-relaxed wrap-anywhere" />
        </li>
      );
    case "thinking":
      return (
        <li data-testid="run-entry" className="flex gap-3">
          {time}
          <p className="min-w-0 flex-1 text-[13px] text-subtle italic wrap-anywhere">Réflexion : {entry.text}</p>
        </li>
      );
    case "action":
      return (
        <li data-testid="run-entry" className="flex gap-3">
          {time}
          <div className="flex min-w-0 flex-1 items-start gap-2 text-[13.5px]">
            <span className={entry.ok ? "mt-0.5 text-success" : "mt-0.5 text-danger"} aria-label={entry.ok ? "Réussi" : "Échec"}>
              {entry.ok ? <IconCheck width={15} height={15} /> : <IconX width={15} height={15} />}
            </span>
            <span className="min-w-0 wrap-anywhere">
              <span className="inline-flex items-center gap-1 font-medium">
                <IconTool width={13} height={13} />
                {toolLabel(entry.tool)}
              </span>
              {entry.summary ? <span className="text-muted"> · {entry.summary}</span> : null}
            </span>
          </div>
        </li>
      );
    case "screenshot": {
      const src = fileUrl(entry.fileKey);
      return (
        <li data-testid="run-entry" className="flex gap-3">
          {time}
          <a href={src} target="_blank" rel="noopener noreferrer" className="block min-w-0 flex-1 overflow-hidden rounded-xl border border-line bg-sunken">
            <img src={src} alt="Capture d'écran" loading="lazy" className="aspect-[16/10] w-full object-contain" />
          </a>
        </li>
      );
    }
    case "question":
      return (
        <li data-testid="run-entry" className="flex gap-3">
          {time}
          <p className="min-w-0 flex-1 rounded-xl bg-warning-soft px-3 py-2 text-[13.5px] text-fg wrap-anywhere">
            <span className="font-medium">Question : </span>
            {entry.text}
          </p>
        </li>
      );
    case "answer":
      return (
        <li data-testid="run-entry" className="flex gap-3">
          {time}
          <p className="min-w-0 flex-1 rounded-xl bg-accent-soft px-3 py-2 text-[13.5px] text-fg wrap-anywhere">
            <span className="font-medium">Ta réponse : </span>
            {entry.text}
          </p>
        </li>
      );
    default:
      return null;
  }
}

function QuestionBox({ run, timeline, onAnswered }: { run: MissionRunView; timeline: RunTimelineEntry[]; onAnswered: () => void }) {
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const answerId = useId();
  const extras = runExtras.parse(run);
  const lastQuestion = [...timeline].reverse().find((e) => e.kind === "question");
  const question = run.question ?? (lastQuestion && lastQuestion.kind === "question" ? lastQuestion.text : null);
  const code = extras.questionCode ?? (question ? /\b(Q\d{1,4})\b/.exec(question)?.[1] : undefined);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const text = answer.trim();
    if (!text || busy) {
      if (!text) setError("Écris ta réponse.");
      return;
    }
    if (text.length > 4000) {
      setError("Ta réponse est trop longue (4 000 caractères maximum).");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const updated = await api("POST /api/runs/:id/answer", {
        params: { id: run.id },
        body: { answer: text, ...(extras.questionId ? { questionId: extras.questionId } : {}) },
      });
      mutateResource<RunDetail>(`run:${run.id}`, (d) => (d ? { ...d, run: updated } : d));
      invalidate("runs");
      setAnswer("");
      onAnswered();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="border-warning/50 p-5">
      <h2 className="font-display text-[20px] leading-snug font-medium">Une question pour toi</h2>
      {question ? <p className="mt-2 text-[14.5px] leading-relaxed whitespace-pre-line wrap-anywhere">{question}</p> : null}
      <form onSubmit={submit} noValidate className="mt-4 space-y-3">
        <Field label="Ta réponse" htmlFor={answerId} error={error}>
          <Textarea id={answerId} value={answer} maxLength={4200} className="min-h-20" onChange={(e) => setAnswer(e.target.value)} />
        </Field>
        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" busy={busy}>
            Répondre
          </Button>
          {code ? <p className="text-[12.5px] text-subtle">Ou par message : {code} &lt;ta réponse&gt;</p> : null}
        </div>
      </form>
    </Card>
  );
}

function ApprovalBox({ approvalId }: { approvalId: string }) {
  const pending = usePendingApprovals();
  const approval: ApprovalView | undefined = pending.data?.find((a) => a.id === approvalId);
  return (
    <Card className="border-warning/50 p-5">
      <h2 className="font-display text-[20px] leading-snug font-medium">Il attend ton accord</h2>
      <div className="mt-3">
        {approval ? (
          <ApprovalCard approval={approval} plain />
        ) : (
          <Link to="/approbations" search={{ focus: approvalId }} className="inline-flex min-h-11 items-center text-[14px] font-semibold text-accent hover:text-accent-hover">
            Voir la demande
          </Link>
        )}
      </div>
    </Card>
  );
}

function CancelRun({ run, onCancelled }: { run: MissionRunView; onCancelled: (run: MissionRunView) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <>
      <Button
        variant="ghost"
        busy={busy}
        title="Arrête la mission à la prochaine étape."
        onClick={async () => {
          setBusy(true);
          setError(null);
          try {
            const updated = await api("POST /api/runs/:id/cancel", { params: { id: run.id }, body: {} });
            onCancelled(updated);
            invalidate("runs");
          } catch (err) {
            setError(err instanceof ApiError && err.status === 409 ? "Cette exécution est déjà terminée." : errorText(err));
          } finally {
            setBusy(false);
          }
        }}
      >
        Annuler
      </Button>
      {error ? (
        <span className="text-[12.5px] text-danger" role="alert">
          {error}
        </span>
      ) : null}
    </>
  );
}
